import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { H1, Body, Card } from '../components/ui';
import { useCurrentBilling } from '../hooks/api/useBilling';
import { CurrencyDollarIcon, UsersIcon, ClockIcon } from '@heroicons/react/24/outline';

const TIER_LABEL: Record<string, string> = {
  seedling: 'Seedling (Free)',
  sprout: 'Sprout',
  // Legacy tiers — surfaced honestly so an admin sees what state the row is in.
  sapling: 'Sapling (legacy plan)',
  canopy: 'Canopy (legacy plan)',
  forest: 'Forest (legacy plan)',
};

const formatZar = (cents: number) => `R${cents.toLocaleString('en-ZA')}`;

const SettingsBilling: React.FC = () => {
  const { data, isLoading, error } = useCurrentBilling();
  const [showAnnual, setShowAnnual] = useState(false);

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto">
        <H1 className="mb-2">Billing</H1>
        <Card className="p-6">
          <Body className="text-gray-500 dark:text-gray-400">Loading projection…</Body>
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto">
        <H1 className="mb-2">Billing</H1>
        <Card className="p-6">
          <Body className="text-red-600 dark:text-red-400">
            Could not load billing preview. Please refresh, or contact support if it keeps failing.
          </Body>
        </Card>
      </div>
    );
  }

  const tierLabel = TIER_LABEL[data.effective_tier] ?? data.effective_tier;
  const isFree = data.billing_model === 'free';
  const isLegacy = data.billing_model === 'legacy';

  const monthly = data.projected_monthly_zar;
  const annualPerMonth = data.projected_annual_zar;
  const displayedAmount = showAnnual ? annualPerMonth : monthly;

  return (
    <div className="max-w-3xl mx-auto">
      <H1 className="mb-2">Billing</H1>
      <Body className="text-gray-600 dark:text-gray-400 mb-8">
        A read-only preview of what this organisation would owe under our published pricing.
        Charges aren't live yet — see the note below.
      </Body>

      {/* Plan + tier */}
      <Card className="p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
            <CurrencyDollarIcon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Current plan</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{tierLabel}</p>
            {data.is_trial && data.trial_ends_at && (
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-secondary-700 dark:text-secondary-300 bg-secondary-50 dark:bg-secondary-900/30 px-2 py-0.5 rounded">
                <ClockIcon className="w-3.5 h-3.5" />
                Trial ends {new Date(data.trial_ends_at).toLocaleDateString('en-ZA')}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Projection */}
      <Card className="p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">This cycle</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {data.active_child_count} active {data.active_child_count === 1 ? 'child' : 'children'} on the roll
            </p>
          </div>
        </div>

        {isFree ? (
          <p className="text-3xl font-display font-bold text-gray-900 dark:text-white">
            {formatZar(0)}
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">— free forever</span>
          </p>
        ) : isLegacy ? (
          <>
            <p className="text-base text-gray-700 dark:text-gray-200">
              Your organisation is on a legacy plan ({tierLabel}). Billing for legacy plans is
              handled out-of-band — please contact us for the canonical figure.
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-3 flex-wrap">
              <p className="text-4xl font-display font-bold text-gray-900 dark:text-white">
                {formatZar(displayedAmount)}
              </p>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                /month{showAnnual ? ', billed annually (10% off)' : ''}
              </span>
              <button
                type="button"
                onClick={() => setShowAnnual(v => !v)}
                className="text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Switch to {showAnnual ? 'monthly' : 'annual'}
              </button>
            </div>

            {/* Math */}
            <div className="mt-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-xs font-mono text-gray-600 dark:text-gray-400 leading-relaxed">
              {(() => {
                const billable = Math.max(0, data.active_child_count - data.minimum_children);
                const start = showAnnual ? data.starting_annual_zar : data.starting_monthly_zar;
                const perChild = showAnnual ? data.per_child_annual_zar : data.per_child_monthly_zar;
                if (billable === 0) {
                  return (
                    <>
                      {data.active_child_count} children · floor R{start} (covers {data.minimum_children}) ={' '}
                      <strong className="text-gray-900 dark:text-white">{formatZar(start)}</strong>
                    </>
                  );
                }
                return (
                  <>
                    R{start} (covers first {data.minimum_children}) + {billable} × R{perChild} ={' '}
                    <strong className="text-gray-900 dark:text-white">{formatZar(displayedAmount)}</strong>
                  </>
                );
              })()}
            </div>
          </>
        )}
      </Card>

      {/* PayStack-not-configured banner */}
      {!data.paystack_configured && (
        <Card className="p-6 border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-900/10">
          <h3 className="text-base font-semibold text-amber-900 dark:text-amber-200 mb-1">
            Billing isn't live yet
          </h3>
          <p className="text-sm text-amber-800 dark:text-amber-200/80 leading-relaxed">
            Charges aren't being processed. The numbers above are what your organisation
            <em> would </em> pay once we wire up PayStack. You'll receive a heads-up email
            before any charge runs. In the meantime,{' '}
            <Link to="/contact" className="underline hover:no-underline">
              get in touch
            </Link>{' '}
            with any billing questions.
          </p>
        </Card>
      )}
    </div>
  );
};

export default SettingsBilling;
